import { FC } from 'react'

import { usePrepareImage } from '~/hooks/usePrepareImage'

import { useActiveProductsContext } from '~/context/ActiveProducts'
import { useNavigation } from '~/context/Navigation'

import { Backdrop } from '~/components/Backdrop'
import { FileInputButton } from '~/components/FileInputButton'
import { Icon } from '~/components/Icon'
import { OnboardingAvatarImage } from '~/components/OnboardingAvatarImage'
import { TextButton } from '~/components/TextButton'

import { validateAvatarImageFile } from '~/modules/Avatar/utils/validateAvatarImageFile'

import theme from '~/theme'

import { translate } from '~/utils/translate'

import * as Styled from './styles'
import { IOnboardingActiveScreenProps } from './types'

export const OnboardingActiveScreen: FC<IOnboardingActiveScreenProps> = ({ visible, handleClose }) => {
  const testID = 'onboarding-active-screen'
  const { navigate } = useNavigation()
  const { products: activeProducts } = useActiveProductsContext()
  const { convertToBase64 } = usePrepareImage()

  const handleCloseFn = () => {
    handleClose()
  }

  const handleFileInputChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event?.target?.files?.length) return

    const file = event.target.files[0]

    validateAvatarImageFile(file)

    const result = await convertToBase64(file)

    localStorage.setItem('@doris_candidate_avatar', result)

    handleCloseFn()

    navigate('ImageInputProcessing')
  }

  const sizingChartHandleButton = () => {
    navigate('SizingSuggestionScreen')
  }

  return (
    <Backdrop
      visible={visible}
      testID={testID}
      handleClose={handleCloseFn}
      withBackground={true}
      containerStyle={{ backgroundColor: theme.colors.white, paddingBottom: '0px' }}
      preventOverflow={false}
      data-testid={`${testID}-backdrop`}
      layerIndex={5}
    >
      <Styled.Container data-testid={`${testID}-content`}>
        <OnboardingAvatarImage useImageWithGrayBorder />

        <Styled.ContentContainer>
          <Styled.DescriptionText
            data-testid={`${testID}-description`}
            align="center"
            dangerouslySetInnerHTML={{
              __html: translate('ONBOARDING_ACTIVE_SCREEN_DESCRIPTION'),
            }}
          />

          <Styled.ChecksIndicators>
            <Styled.IndicatorOption data-testid={`${testID}-indicator-option-1`}>
              <Icon name="circleCheck" size="12px" color="#81CB31" />{' '}
              <span>{translate('ONBOARDING_ACTIVE_SCREEN_CHECK_1')}</span>
            </Styled.IndicatorOption>

            <Styled.IndicatorOption data-testid={`${testID}-indicator-option-2`}>
              <Icon name="circleCheck" size="12px" color="#81CB31" />{' '}
              <span>{translate('ONBOARDING_ACTIVE_SCREEN_CHECK_2')}</span>
            </Styled.IndicatorOption>

            <Styled.IndicatorOption data-testid={`${testID}-indicator-option-3`}>
              <Icon name="circleCheck" size="12px" color="#81CB31" />{' '}
              <span>{translate('ONBOARDING_ACTIVE_SCREEN_CHECK_3')}</span>
            </Styled.IndicatorOption>
          </Styled.ChecksIndicators>

          <Styled.ButtonsContainer>
            <FileInputButton
              icon="imageUpload"
              name="avatar-upload"
              onChange={handleFileInputChange}
              accept=".heic, .png, .jpg, .jpeg"
            >
              {translate('ONBOARDING_SCREEN_BUTTON_SEND_PHOTO')}
            </FileInputButton>

            <TextButton
              testID={`${testID}-secondary-button`}
              onClick={sizingChartHandleButton}
              background={theme.colors.neutral.black100}
              textColor={theme.colors.primary}
              disabled={!activeProducts.filter(p => p.has_size).length}
            >
              {translate('ONBOARDING_SCREEN_BUTTON_SIZING_TABLE')}
            </TextButton>
          </Styled.ButtonsContainer>

          <Styled.TermsContainer>
            <Icon name="locked" size={'16px'} color={theme.colors.neutral.black600} />
            <Styled.Text
              data-testid={`${testID}-terms`}
              align="center"
              dangerouslySetInnerHTML={{
                __html: translate('ONBOARDING_SCREEN_TERMS'),
              }}
            />
          </Styled.TermsContainer>
        </Styled.ContentContainer>
      </Styled.Container>
    </Backdrop>
  )
}
